import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { useAuth } from '../lib/auth';
import { useAppSession } from './useAppSession';
import { figmaApi } from '../lib/api';
import { DEBUG_FIX_ENABLED } from '../lib/figmaDebugFlag';
import { log } from '@/lib/logger';

log.info('[useFigmaToken] Module loaded');

/**
 * Scoped debug logger for the dashboard Figma OAuth flow.
 * Kept local so the migration from the legacy token-entry flow stays easy to remove.
 */
function logFigmaDashboardDebug(message: string, context?: Record<string, unknown>): void {
  if (!DEBUG_FIX_ENABLED) {
    return;
  }
  log.warn('[OverlayQA DEBUG_FIX DashboardFigmaIntegration]', { message, context: context ?? {} });
}

export function useFigmaToken() {
  const { data: session } = useAppSession();
  // The server resolves the userId from the auth context on /figma/tokens/me,
  // so we no longer pass it in the URL. We still key the query on dbUserId
  // so the cache flushes cleanly when the user switches accounts.
  const dbUserId = session?.user?.id;
  log.info('[useFigmaToken] useFigmaToken called, userId:', { dbUserId });

  return useApiQuery({
    queryKey: ['figmaToken', dbUserId],
    queryFn: async (token) => {
      log.info('[useFigmaToken] queryFn executing for userId:', { dbUserId });
      if (!dbUserId) throw new Error('User not found');
      return figmaApi.getToken(token);
    },
    enabled: !!dbUserId,
  });
}

/**
 * Connect Figma via OAuth popup.
 * Uses manual auth because the popup close handler needs queryClient
 * for invalidation inside the Promise body (not just onSuccess).
 */
export function useConnectFigma() {
  const { getToken } = useAuth();
  // The OAuth route (POST /api/figma/oauth/auth) validates `userId` with
  // z.string().uuid() AND requires it to equal the auth-context DB userId.
  // The working extension flow sends the internal DB user id; Clerk's
  // useUser().id is "user_…" (not a UUID) and 400'd every dashboard connect
  // before a popup ever opened (session 019ecc63, 2026-06-16). Use the same
  // DB user id the extension does — from the app session, not Clerk.
  const { data: session } = useAppSession();
  const dbUserId = session?.user?.id;
  const queryClient = useQueryClient();

  log.info('[useFigmaToken] useConnectFigma called');

  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      if (!dbUserId) throw new Error('User not found');

      logFigmaDashboardDebug('Requesting Figma auth URL', {
        hasAuthToken: Boolean(token),
        userId: dbUserId,
      });

      const { url, state } = await figmaApi.getAuthUrl(token, dbUserId);
      sessionStorage.setItem('figma_oauth_state', state);

      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        url,
        'figma_oauth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      logFigmaDashboardDebug('Opened Figma OAuth popup', {
        popupName: 'figma_oauth',
      });

      return new Promise<void>((resolve, reject) => {
        const timeoutId = window.setTimeout(() => {
          window.clearInterval(checkInterval);
          if (!popup.closed) {
            popup.close();
          }
          reject(new Error('OAuth timed out'));
        }, 5 * 60 * 1000);

        const checkInterval = window.setInterval(() => {
          try {
            if (popup.closed) {
              window.clearInterval(checkInterval);
              window.clearTimeout(timeoutId);
              queryClient.invalidateQueries({ queryKey: ['figmaToken'] });
              logFigmaDashboardDebug('Figma OAuth popup closed, invalidating token query', {
                userId: dbUserId,
              });
              resolve();
            }
          } catch {
            logFigmaDashboardDebug('Figma OAuth popup cross-origin redirect observed');
          }
        }, 500);
      });
    },
    onSuccess: () => {
      logFigmaDashboardDebug('Figma OAuth completed');
    },
    onError: (error) => {
      logFigmaDashboardDebug('Figma OAuth failed', {
        error: error instanceof Error ? error.message : String(error),
      });
    },
  });
}

export function useRemoveFigmaToken() {
  const { data: session } = useAppSession();
  const dbUserId = session?.user?.id;
  log.info('[useFigmaToken] useRemoveFigmaToken called, userId:', { dbUserId });

  return useApiMutation<unknown, void>({
    mutationFn: async (token) => {
      log.info('[useFigmaToken] removeToken executing for userId:', { dbUserId });
      if (!dbUserId) throw new Error('User not found');
      return figmaApi.removeToken(token);
    },
    invalidateKeys: [['figmaToken']],
    onSuccess: () => {
      logFigmaDashboardDebug('Figma token removed, invalidating token query', {
        userId: dbUserId ?? null,
      });
    },
  });
}
